import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import PageFooter from '@/components/feature/PageFooter';

interface PointResult {
  user_id: string;
  member_name?: string;
  point: number;
  status: 'success' | 'error';
  message?: string;
  rawParams: Record<string, string>;
}

export default function ErpCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<PointResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isIframe = searchParams.get('iframe') === '1';

  useEffect(() => {
    // 모든 URL 파라미터 수집 (서식 표시용)
    const rawParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      rawParams[key] = value;
    });

    // ERP 서버가 돌려주는 파라미터 이름들을 모두 시도
    const userId =
      searchParams.get('user_id') ||
      searchParams.get('userid') ||
      searchParams.get('member_id') ||
      searchParams.get('id') || '';

    const memberName =
      searchParams.get('member_name') ||
      searchParams.get('name') ||
      searchParams.get('user_name') || '';

    // 포인트 파라미터 이름 다양하게 시도
    const pointStr =
      searchParams.get('point') ||
      searchParams.get('points') ||
      searchParams.get('balance') ||
      searchParams.get('point_balance') ||
      searchParams.get('mileage') ||
      searchParams.get('result_point') || null;

    const message = searchParams.get('message') || searchParams.get('msg') || '';

    let parsed: PointResult;

    if (userId && pointStr !== null) {
      const point = parseFloat(pointStr);
      parsed = {
        user_id: userId,
        member_name: memberName || undefined,
        point: isNaN(point) ? 0 : point,
        status: 'success',
        rawParams,
      };
    } else if (userId) {
      parsed = {
        user_id: userId,
        member_name: memberName || undefined,
        point: 0,
        status: 'success',
        rawParams,
      };
    } else {
      parsed = {
        user_id: '',
        point: 0,
        status: 'success',
        message: message || undefined,
        rawParams,
      };
    }

    setResult(parsed);
    setIsLoading(false);

    // iframe 모드: 부모 창에 postMessage로 결과 전달
    if (isIframe && window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'ERP_POINT_RESULT',
          user_id: parsed.user_id,
          point: parsed.point,
          status: parsed.status,
          message: parsed.message,
        },
        window.location.origin
      );
    }
  }, [searchParams, isIframe]);

  const formatPrice = (n: number) =>
    parseFloat(n.toFixed(2)).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // iframe 모드: 최소한의 UI만 표시
  if (isIframe) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center p-8">
          {isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-emerald-600 text-3xl mb-3 block"></i>
              <p className="text-sm text-gray-500">처리 중...</p>
            </>
          ) : (
            <>
              <i className="ri-check-line text-emerald-600 text-3xl mb-3 block"></i>
              <p className="text-sm text-gray-500">결과 전달 완료</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const hasParams = result && Object.keys(result.rawParams).length > 0;
  const hasUserId = result && result.user_id;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pt-20 pb-16">
        {/* 헤더 배너 */}
        <div className="relative overflow-hidden h-[200px] md:h-[260px]">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/decfaa9e67ddbf0507b7bf4a8f6a2087.jpg"
              alt="ERP 포인트 조회 결과"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 via-emerald-800/70 to-teal-800/60"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14 text-white">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-400/30 text-emerald-100 border border-emerald-400/40">
                ERP 포인트 연동
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                결과 수신 페이지
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              포인트 조회 결과
            </h1>
            <p className="text-emerald-100 text-sm">ERP 전산 시스템에서 전달된 포인트 정보입니다.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-5">
          {isLoading ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <i className="ri-loader-4-line animate-spin text-emerald-600 text-4xl mb-4 block"></i>
              <p className="text-gray-600 font-medium">포인트 정보를 불러오는 중...</p>
            </div>
          ) : (
            <>
              {/* ── 결과 카드 ── */}
              {hasUserId ? (
                <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 md:p-8 text-white">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    {/* 회원 정보 */}
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                        <i className="ri-user-3-line text-white text-3xl"></i>
                      </div>
                      <div>
                        <p className="text-emerald-200 text-xs font-semibold mb-0.5">조회된 회원</p>
                        {result.member_name && (
                          <p className="text-base font-bold text-white mb-0.5">{result.member_name}</p>
                        )}
                        <p className="text-xl font-black font-mono tracking-wider">{result.user_id}</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/30 text-emerald-100 text-xs font-bold border border-emerald-400/40">
                            <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center"></i>
                            ERP 전달 완료
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 포인트 */}
                    <div className="text-center sm:text-right flex-shrink-0">
                      <p className="text-emerald-200 text-xs font-semibold mb-1">전달된 포인트</p>
                      <p className="text-5xl md:text-6xl font-black leading-none">{formatPrice(result.point)}</p>
                      <p className="text-emerald-300 text-sm font-bold mt-1">P</p>
                    </div>
                  </div>
                </div>
              ) : (
                /* 회원ID 없이 도착한 경우 */
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                      <i className="ri-information-line text-amber-600 text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1">결과 수신 대기 중</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        ERP 전산에서 포인트 조회 후 이 페이지로 결과가 전달됩니다.
                        현재 전달된 회원 정보가 없습니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── 수신된 파라미터 서식 표시 ── */}
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  <i className="ri-code-s-slash-line w-5 h-5 flex items-center justify-center text-emerald-600"></i>
                  수신된 파라미터 (결과값)
                </h2>
                <p className="text-xs text-gray-400 mb-5">
                  ERP 전산에서 이 페이지로 전달된 URL 파라미터 전체 목록입니다.
                </p>

                {hasParams ? (
                  <div className="space-y-2">
                    {/* 주요 파라미터 강조 표시 */}
                    <div className="grid sm:grid-cols-2 gap-3 mb-4">
                      {[
                        { keys: ['user_id', 'userid', 'member_id', 'id'], label: '회원 ID', icon: 'ri-id-card-line', color: 'emerald' },
                        { keys: ['member_name', 'name', 'user_name'], label: '회원 이름', icon: 'ri-user-line', color: 'teal' },
                        { keys: ['point', 'points', 'balance', 'point_balance', 'mileage', 'result_point'], label: '포인트', icon: 'ri-coin-line', color: 'amber' },
                        { keys: ['status', 'result', 'success'], label: '처리 상태', icon: 'ri-checkbox-circle-line', color: 'green' },
                      ].map((field) => {
                        const matchedKey = field.keys.find((k) => result?.rawParams[k] !== undefined);
                        const value = matchedKey ? result?.rawParams[matchedKey] : null;
                        return (
                          <div
                            key={field.label}
                            className={`rounded-xl p-4 border-2 ${
                              value
                                ? field.color === 'emerald' ? 'bg-emerald-50 border-emerald-200'
                                : field.color === 'teal' ? 'bg-teal-50 border-teal-200'
                                : field.color === 'amber' ? 'bg-amber-50 border-amber-200'
                                : 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200 opacity-60'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <i className={`${field.icon} w-4 h-4 flex items-center justify-center ${
                                value
                                  ? field.color === 'emerald' ? 'text-emerald-600'
                                  : field.color === 'teal' ? 'text-teal-600'
                                  : field.color === 'amber' ? 'text-amber-600'
                                  : 'text-green-600'
                                  : 'text-gray-400'
                              }`}></i>
                              <span className="text-xs font-bold text-gray-500">{field.label}</span>
                              {matchedKey && (
                                <code className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1 rounded">{matchedKey}</code>
                              )}
                            </div>
                            {value ? (
                              <p className={`text-lg font-black font-mono ${
                                field.color === 'emerald' ? 'text-emerald-700'
                                : field.color === 'teal' ? 'text-teal-700'
                                : field.color === 'amber' ? 'text-amber-700'
                                : 'text-green-700'
                              }`}>
                                {field.label === '포인트' ? `${formatPrice(parseFloat(value) || 0)}P` : value}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400 italic">전달되지 않음</p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* 전체 파라미터 원본 */}
                    <div className="bg-slate-900 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                          <i className="ri-terminal-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          수신된 전체 파라미터 (원본)
                        </p>
                        <span className="text-xs text-slate-500 font-mono">
                          {Object.keys(result?.rawParams || {}).length}개 파라미터
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {Object.entries(result?.rawParams || {}).map(([key, value]) => (
                          <div key={key} className="flex items-start gap-3">
                            <code className="text-xs font-mono text-emerald-400 flex-shrink-0 min-w-[120px]">{key}</code>
                            <span className="text-slate-400 flex-shrink-0">:</span>
                            <code className="text-xs font-mono text-slate-200 break-all">{value}</code>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 수신 URL */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1.5">
                        <i className="ri-link-m w-3.5 h-3.5 flex items-center justify-center text-gray-400"></i>
                        수신된 전체 URL
                      </p>
                      <code className="text-xs font-mono text-gray-600 break-all leading-relaxed">
                        {window.location.href}
                      </code>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                      <i className="ri-inbox-line text-gray-400 text-2xl"></i>
                    </div>
                    <p className="text-sm font-bold text-gray-500 mb-1">수신된 파라미터 없음</p>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      ERP 전산에서 포인트 조회 후 결과를 이 페이지로 전달하면 여기에 표시됩니다.
                    </p>
                  </div>
                )}
              </div>

              {/* ── 안내 카드 ── */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
                    <i className="ri-information-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm mb-1">이 페이지는 ERP 결과 수신 전용입니다</p>
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">
                      ERP 전산 프로그램에서 포인트 조회 후 <code className="font-mono bg-gray-100 px-1 rounded text-emerald-700">return_page</code>로 설정된 이 주소로 결과가 전달됩니다.
                      포인트 잔액을 직접 조회하시려면 아래 버튼을 이용해주세요.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => navigate('/my-points')}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-coin-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        포인트 직접 조회하기
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/erp-guide')}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-book-open-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        ERP 연동 가이드
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── 포인트 있으면 바로 구매 유도 ── */}
              {hasUserId && result.point > 0 && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="flex-1 text-center sm:text-left">
                      <p className="text-sm font-bold text-emerald-800 mb-1">
                        <span className="font-black text-emerald-600">{formatPrice(result.point)}P</span> 포인트로 바로 구매하세요!
                      </p>
                      <p className="text-xs text-emerald-600">
                        포인트 조회 페이지에서 구매 가능한 제품을 바로 확인할 수 있습니다.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/my-points')}
                      className="flex-shrink-0 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center"></i>
                      포인트로 구매하기
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <PageFooter />
    </>
  );
}
